import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Card, CircularProgress, Grid } from '@material-ui/core';
import rightArrowIcon from 'core/assets/svg/ic-right-arrow.svg';
import greyArrow from 'core/assets/svg/icGreyArrowRight.svg';

import { Label } from 'core/components/label';
import ValueField from 'core/components/value-field';
import { Button } from 'core/components/button';
import { dateTimeFormatterInUserTimezone } from 'core/helpers/formatters';
import { GET_OFFER, GET_OFFER_DIFFERENCE } from 'core/store/offer-store/offer-queries';
import useStyles from './compare-parent-offer.styles';

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\./gi;

// Path prefixes, suffixes, or substrings to exclude from the diff — add here as needed.
const SKIP_PATH_PREFIXES = ['options.0.', 'options.1.', 'options.2.', 'options.3.'];
const SKIP_PATH_SUFFIXES = ['.amount'];
const SKIP_PATH_CONTAINS = ['replacementCostEstimate.itemized'];
const removeUnwantedKeys = (path) =>
  SKIP_PATH_PREFIXES.some((p) => path.startsWith(p)) ||
  SKIP_PATH_SUFFIXES.some((s) => path.endsWith(s)) ||
  SKIP_PATH_CONTAINS.some((s) => path.includes(s));

//removes UUID from key so it is not displayed in diff
const removeUUIDFromKey = (path) => path.replace(UUID_PATTERN, '');

//used for the highlight in the diff
const getInlineDiff = (prev = '', curr = '') => ({
  prev: [{ text: prev, highlight: true }],
  curr: [{ text: curr, highlight: true }]
});

const OfferCompare = () => {
  const classes = useStyles();
  const { offerId } = useParams();

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError
  } = useQuery(GET_OFFER, {
    variables: { offerId },
    fetchPolicy: 'no-cache'
  });

  const currentOffer = currentData?.offer;
  const parentOfferId = currentOffer?.parentId;

  const { data: parentData, loading: parentLoading } = useQuery(GET_OFFER, {
    variables: { offerId: parentOfferId },
    skip: !parentOfferId,
    fetchPolicy: 'no-cache'
  });

  const {
    data: diffData,
    loading: diffLoading,
    error: diffError
  } = useQuery(GET_OFFER_DIFFERENCE, {
    variables: { oldOfferId: parentOfferId, newOfferId: offerId },
    skip: !parentOfferId,
    fetchPolicy: 'network-only'
  });

  const parentOffer = parentData?.offer;

  const onViewPreviousChanges = useCallback(() => {
    if (!parentOffer?.id) {
      return;
    }
    window.open(`/offer/${parentOffer.id}/compare`, '_blank');
  }, [parentOffer]);

  const buildChanges = () => {
    const result = diffData?.getOfferDifference;
    if (!result?.success || !result.difference) {
      return [];
    }
    const addedChanges = JSON.parse(result.difference.addedChanges ?? '{}');
    const removedChanges = JSON.parse(result.difference.removedChanges ?? '{}');
    const allKeys = new Set([...Object.keys(addedChanges), ...Object.keys(removedChanges)]);
    return Array.from(allKeys)
      .filter((path) => !removeUnwantedKeys(path))
      .map((path) => ({
        path,
        previousValue: String(removedChanges[path] ?? 'null'),
        currentValue: String(addedChanges[path] ?? 'null')
      }));
  };

  if (currentLoading) {
    return (
      <div className={classes.page}>
        <CircularProgress />
      </div>
    );
  }

  if (currentError || !currentOffer) {
    return (
      <div className={classes.errorContainer}>
        <Label type="title">Offer not found</Label>
        <Label type="body1">
          The offer could not be loaded. It may be invalid or you may not have permission to view it.
        </Label>
        <Label type="body1" className={classes.errorId}>
          {offerId}
        </Label>
      </div>
    );
  }

  if (!parentOfferId) {
    return (
      <div className={classes.errorContainer}>
        <Label type="title">No previous offer</Label>
        <Label type="body1">
          Offer <strong>{offerId}</strong> does not have a parent offer ID — there is no previous version to compare
          against.
        </Label>
      </div>
    );
  }

  const changes = buildChanges();
  const coApplicant = currentOffer.quote?.drivers?.find((d) => d.isCoApplicant);
  const isLoading = parentLoading || diffLoading;
  const hasError = diffError || diffData?.getOfferDifference?.success === false;

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <Label type="title">Compare to parent offer</Label>
        {/* header */}
        {currentOffer?.webUserId && (
          <Grid container item direction="row" alignItems="baseline" className={classes.metaRow}>
            <Label className={classes.subtitle} type="infoLabel">
              Pre-bind ID
            </Label>
            <Label type="infoValue">
              <Grid container alignItems="center">
                {currentOffer.webUserId}
              </Grid>
            </Label>
          </Grid>
        )}
        <Grid container item direction="row" alignItems="baseline" className={classes.metaRow}>
          <Label className={classes.subtitle} type="infoLabel">
            Applicant
          </Label>
          <Label type="infoValue">
            {currentOffer.quote?.fname} {currentOffer.quote?.lname}
          </Label>
        </Grid>
        <Grid container item direction="row" alignItems="baseline" className={classes.metaRow}>
          <Label className={classes.subtitle} type="infoLabel">
            Co-applicant
          </Label>
          <Label type="infoValue">{coApplicant ? `${coApplicant.firstName} ${coApplicant.lastName}` : 'N/A'}</Label>
        </Grid>
        <Card className={classes.offerIdCard}>
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Grid item container direction="row" alignItems="center" className={classes.offerIdPair}>
              <Grid item className={classes.offerIdBlock}>
                {parentOffer?.updatedDateTime && (
                  <Label type="infoLabel" className={classes.offerDateLabel}>
                    {dateTimeFormatterInUserTimezone(parentOffer.updatedDateTime, {
                      dateOptions: { month: 'short', day: 'numeric' },
                      timeOptions: { timeZoneName: 'short' }
                    })}
                  </Label>
                )}
                <ValueField
                  label="Parent offer ID"
                  value={parentOfferId}
                  copyable
                  className={classes.offerIdKeyLabel}
                />
              </Grid>
              <Grid item className={classes.offerIdArrow}>
                <img src={greyArrow} alt="to" />
              </Grid>
              <Grid item className={classes.offerIdBlock}>
                {currentOffer.updatedDateTime && (
                  <Label type="infoLabel" className={classes.offerDateLabel}>
                    {dateTimeFormatterInUserTimezone(currentOffer.updatedDateTime, {
                      dateOptions: { month: 'short', day: 'numeric' },
                      timeOptions: { timeZoneName: 'short' }
                    })}
                  </Label>
                )}
                <ValueField label="Offer ID" value={offerId} copyable className={classes.offerIdKeyLabel} />
              </Grid>
            </Grid>
            {parentOffer?.parentId && (
              <Button
                variant="text"
                color="secondary"
                onClick={onViewPreviousChanges}
                className={classes.previousChangesButton}
              >
                <img src={rightArrowIcon} alt="previous" className={classes.previousChangesArrow} />
                View previous changes
              </Button>
            )}
          </Grid>
        </Card>
      </div>

      {isLoading && <CircularProgress />}

      {!isLoading && hasError && (
        <div className={classes.noChanges}>
          <Label type="body1">
            {diffData?.getOfferDifference?.error ?? 'The offer difference could not be loaded.'}
          </Label>
        </div>
      )}

      {!isLoading && !hasError && changes.length === 0 && (
        <Label type="body1" className={classes.noChanges}>
          No differences found between these two offers.
        </Label>
      )}

      {/* table */}
      {!isLoading && !hasError && changes.length > 0 && (
        <div className={classes.tableWrapper}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>
                  Parent offer values
                  <br />({parentOfferId})
                </th>
                <th className={classes.th}>
                  Offer values
                  <br />({offerId})
                </th>
              </tr>
            </thead>
            <tbody>
              {changes.map(({ path, previousValue, currentValue }) => {
                const diff = getInlineDiff(previousValue, currentValue);
                return (
                  <tr key={path}>
                    <td className={`${classes.td} ${classes.tdPrevious}`}>
                      <span className={classes.fieldPath}>{removeUUIDFromKey(path)}:</span>{' '}
                      {diff.prev.map(({ text, highlight }, i) =>
                        highlight ? (
                          <mark key={i} className={classes.highlightPrevious}>
                            {text}
                          </mark>
                        ) : (
                          text
                        )
                      )}
                      ,
                    </td>
                    <td className={`${classes.td} ${classes.tdCurrent}`}>
                      <span className={classes.fieldPath}>{removeUUIDFromKey(path)}:</span>{' '}
                      {diff.curr.map(({ text, highlight }, i) =>
                        highlight ? (
                          <mark key={i} className={classes.highlightCurrent}>
                            {text}
                          </mark>
                        ) : (
                          text
                        )
                      )}
                      ,
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OfferCompare;
